/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import { Box, Button, Spinner } from '@chakra-ui/react'
import { getAssetsBatchDataByBatch, getAssetsBatchDataByBatchAsCsv, retryBatchProcess } from '@/services/asset-batches'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import DefaultCrudTable from '@/components/tables/DefaultCrudTable'
import CustomTableBody from './components/CustomTableBody'
import DetailsModal from './components/DetailsModal'
import EditDataModal from '../Validations/components/EditDataModal'
import { retryAsset } from '@/services/assets'
import RetryModal from './components/RetryModal'
import { useAuth0 } from '@auth0/auth0-react'
import { RETRY_BATCH } from '@/configs/strings/general-batch'
import { ASSET_PROCESSING } from '@/configs/strings/register-multiple-assets'
import { columnsFileFromCsv } from '../../handlers'
import { filterOptions, sortOptions } from './handlers'
import FilterAndSortBarWithActions from '@/components/listings/FilterAndSortBarWithActions'
import DownloadFileButton from '@/components/buttons/DownloadFileButton'
import { EXPORT_CSV } from '@/configs/strings/listings'

const Processing = ({
  batchHasErrors,
  batchId,
  batchIsProcessing,
  batchName
}) => {
  const [detailsModalIsOpen, setDetailsModalIsOpen] = useState(false)
  const [editModalIsOpen, setEditModalIsOpen] = useState(false)
  const [retryModalIsOpen, setRetryModalIsOpen] = useState(false)
  const [currentData, setCurrentData] = useState()
  const [retryBatch, setRetryBatch] = useState()
  const [retryProcess, setRetryProcess] = useState()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const {
    itemsResponse,
    loading,
    onChangePage,
    page,
    reloadItems,
    searchParams,
    setSearchParams,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getAssetsBatchDataByBatch,
    fetchParams: { id: batchId }
  })
  const onOpenModal = (assetData) => {
    setDetailsModalIsOpen(true)
    setCurrentData(assetData)
  }
  const onCloseModal = () => {
    setDetailsModalIsOpen(false)
    setCurrentData(null)
  }
  const onOpenEditModal = (assetData) => {
    setCurrentData(assetData)
    setEditModalIsOpen(true)
  }
  const onCloseEditModal = () => {
    reloadItems()
    setEditModalIsOpen(false)
    setCurrentData(null)
  }
  const onOpenRetryModal = (assetData) => {
    setRetryProcess(() => retryAsset)
    setCurrentData(assetData)
    setRetryModalIsOpen(true)
  }
  const onCloseRetryModal = () => {
    reloadItems()
    setRetryModalIsOpen(false)
    setCurrentData(null)
    setRetryBatch(false)
  }
  const onOpenRetryBatchModal = () => {
    setRetryProcess(() => retryBatchProcess)
    setRetryModalIsOpen(true)
    setRetryBatch(true)
  }

  if (loading) {
    return (
      <div className="text-center">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      {editModalIsOpen && (
        <EditDataModal
          batchId={batchId}
          dataId={currentData.id}
          data={currentData}
          onClose={onCloseEditModal}
          getAccessToken={getAccessToken}
        />
      )}
      {detailsModalIsOpen &&
        <DetailsModal
          assetData={currentData}
          batchId={batchId}
          onClose={onCloseModal}
        />
      }
      {retryModalIsOpen &&
        <RetryModal
          batchId={batchId}
          batchName={batchName}
          getAccessToken={getAccessToken}
          onClose={onCloseRetryModal}
          onRetry={retryProcess}
          retryBatch={retryBatch}
          assetData={currentData}
        />
      }
      <section>
        <h2 className="mb-4">{ ASSET_PROCESSING }</h2>
        <Box mb="4">
          <DownloadFileButton
            service={
              (getAccessToken) =>
                getAssetsBatchDataByBatchAsCsv({ id: batchId }, getAccessToken)
            }
            isDisabled={!itemsResponse?.data.length}
              dataKey="csv"
          >
            { EXPORT_CSV }
          </DownloadFileButton>
        </Box>
        <Box mb="3">
          <FilterAndSortBarWithActions
            currentValues={searchParams}
            filterOptions={filterOptions}
            onReset={() => setSearchParams({})}
            page={page}
            sortOptions={sortOptions}
            onFilterResults={(filter) => setSearchParams(filter)}
          />
        </Box>
        { batchHasErrors &&
          <div className="mt-4 mb-4">
            <Button onClick={onOpenRetryBatchModal}>
              { RETRY_BATCH }
            </Button>
          </div>
        }
        <DefaultCrudTable
          page={page}
          totalPages={totalPages}
          onPageChange={(page) => onChangePage(page)}
        >
          <DefaultCrudTable.Header
            columns={columnsFileFromCsv}
          />
          <CustomTableBody
            loading={loading}
            columns={columnsFileFromCsv}
            items={itemsResponse.data}
            onDetails={onOpenModal}
            onRetry={!batchIsProcessing && onOpenRetryModal}
            onFix={onOpenEditModal}
            getAccessToken={getAccessToken}
          />
        </DefaultCrudTable>
      </section>
    </>
  )
}

export default Processing
